import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  listAccompanyShoppingOrders, 
  listProviderAccompanyShoppingOrders,
  getAccompanyShoppingOrderById,
  updateAccompanyShoppingOrderStatus,
  updateAccompanyShoppingOrder
} from '../../api/accompanyShoppingOrder';

// 订单状态映射
export const ORDER_STATUS_MAP = {
  0: '待确认',
  1: '已确认',
  2: '服务中',
  3: '已完成',
  4: '已取消',
  5: '已退款',
  6: '异常'
};

// 服务类别映射（假设）
export const SERVICE_CATEGORY_MAP = {
  1: '基础陪逛服务',
  2: '高级陪逛服务',
  3: '定制陪逛服务'
};

// 分页查询陪逛服务订单的异步 thunk
export const fetchAccompanyShoppingOrders = createAsyncThunk(
  'accompanyShoppingOrder/fetchOrders',
  async (params, { rejectWithValue }) => {
    try {
      const response = await listAccompanyShoppingOrders(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 查询服务提供者的陪逛服务订单的异步 thunk
export const fetchProviderAccompanyShoppingOrders = createAsyncThunk(
  'accompanyShoppingOrder/fetchProviderOrders',
  async ({ providerId, ...params }, { rejectWithValue }) => {
    try {
      const response = await listProviderAccompanyShoppingOrders(providerId, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 获取单个陪逛服务订单详情的异步 thunk
export const fetchAccompanyShoppingOrderDetail = createAsyncThunk(
  'accompanyShoppingOrder/fetchOrderDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getAccompanyShoppingOrderById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 更新陪逛服务订单状态的异步 thunk
export const updateOrderStatus = createAsyncThunk(
  'accompanyShoppingOrder/updateOrderStatus',
  async ({ orderId, status }, { rejectWithValue }) => {
    try {
      const response = await updateAccompanyShoppingOrderStatus(orderId, status);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 更新陪逛服务订单信息的异步 thunk
export const updateOrder = createAsyncThunk(
  'accompanyShoppingOrder/updateOrder',
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await updateAccompanyShoppingOrder(orderData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const accompanyShoppingOrderSlice = createSlice({
  name: 'accompanyShoppingOrder',
  initialState: {
    orders: [],
    total: 0,
    current: 1,
    pageSize: 10,
    currentOrder: null,
    loading: false,
    error: null
  },
  reducers: {},
  extraReducers: (builder) => {
    // 处理分页查询陪逛服务订单的 reducer
    builder.addCase(fetchAccompanyShoppingOrders.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAccompanyShoppingOrders.fulfilled, (state, action) => {
      state.loading = false;
      state.orders = action.payload.records || [];
      state.total = action.payload.total || 0;
      state.current = action.payload.current || 1;
      state.pageSize = action.payload.size || 10;
    });
    builder.addCase(fetchAccompanyShoppingOrders.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 处理查询服务提供者陪逛服务订单的 reducer
    builder.addCase(fetchProviderAccompanyShoppingOrders.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchProviderAccompanyShoppingOrders.fulfilled, (state, action) => {
      state.loading = false;
      state.orders = action.payload.records || [];
      state.total = action.payload.total || 0;
      state.current = action.payload.current || 1;
      state.pageSize = action.payload.size || 10;
    });
    builder.addCase(fetchProviderAccompanyShoppingOrders.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 处理获取订单详情的 reducer
    builder.addCase(fetchAccompanyShoppingOrderDetail.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAccompanyShoppingOrderDetail.fulfilled, (state, action) => {
      state.loading = false;
      state.currentOrder = action.payload;
    });
    builder.addCase(fetchAccompanyShoppingOrderDetail.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 处理更新订单状态的 reducer
    builder.addCase(updateOrderStatus.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateOrderStatus.fulfilled, (state, action) => {
      state.loading = false;
      // 更新当前订单或列表中的订单
      if (state.currentOrder && state.currentOrder.id === action.payload.id) {
        state.currentOrder = action.payload;
      }
      const index = state.orders.findIndex(order => order.id === action.payload.id);
      if (index !== -1) {
        state.orders[index] = action.payload;
      }
    });
    builder.addCase(updateOrderStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 处理更新订单信息的 reducer
    builder.addCase(updateOrder.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateOrder.fulfilled, (state, action) => {
      state.loading = false;
      // 更新当前订单或列表中的订单
      if (state.currentOrder && state.currentOrder.id === action.payload.id) {
        state.currentOrder = action.payload;
      }
      const index = state.orders.findIndex(order => order.id === action.payload.id);
      if (index !== -1) {
        state.orders[index] = action.payload;
      }
    });
    builder.addCase(updateOrder.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  }
});

export default accompanyShoppingOrderSlice.reducer; 